import { useShow, useCustomMutation } from "@refinedev/core";
import { Show } from "@refinedev/antd";
import { Button, Descriptions, Select, Space, Typography, message } from "antd";
import { useMemo, useState } from "react";
import { apiUrl } from "../../consts";
import { IconDisplay } from "../../components/IconDisplay";
import { formatSchedule } from "./schedule";

type TournamentStatus = "DRAFT" | "PUBLISHED" | "DELETED";

export function TournamentShow() {
  const { queryResult } = useShow({
    resource: "tournament",
  });

  const record = (queryResult.data?.data as any)?.data;

  const initialStatus: TournamentStatus | undefined = record?.status;
  const [status, setStatus] = useState<TournamentStatus | undefined>(initialStatus);

  const { mutate: mutateStatus, isLoading } = useCustomMutation();

  const canSave = useMemo(() => {
    if (!record?.id) return false;
    if (!status) return false;
    return status !== record?.status;
  }, [record?.id, record?.status, status]);

  return (
    <Show isLoading={queryResult.isLoading} title="Tournament">
      {record ? (
        <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ minWidth: 120 }}>
            <IconDisplay iconURL={record.thumbnailURL} width={120} height={120} borderRadius="8px" />
          </div>

          <div style={{ flex: 1, minWidth: 320 }}>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Name">{record.name}</Descriptions.Item>
              <Descriptions.Item label="Creator">{record.creatorName}</Descriptions.Item>
              <Descriptions.Item label="Mode">{record.tournamentMode}</Descriptions.Item>
              <Descriptions.Item label="Start">
                {record.startDateTime ? new Date(record.startDateTime).toLocaleString() : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Schedule">
                {formatSchedule(record.recurring) || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Product">{record.productName || "-"}</Descriptions.Item>
              <Descriptions.Item label="Type">{record.tournamentTypeName || "-"}</Descriptions.Item>
              <Descriptions.Item label="Created">
                {record.createdAt ? new Date(record.createdAt).toLocaleString() : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Select<TournamentStatus>
                    value={status}
                    onChange={(v) => setStatus(v)}
                    style={{ width: 220 }}
                    options={[
                      { value: "PUBLISHED", label: "PUBLISHED" },
                      { value: "DRAFT", label: "DRAFT" },
                      { value: "DELETED", label: "DELETED" },
                    ]}
                  />
                  <Button
                    type="primary"
                    disabled={!canSave}
                    loading={isLoading}
                    onClick={() => {
                      mutateStatus(
                        {
                          url: `${apiUrl}/tournament/${record.id}/status`,
                          method: "patch",
                          values: { status: status as "DRAFT" | "PUBLISHED" | "DELETED" },
                        },
                        {
                          onSuccess: (res) => {
                            message.success(res?.data?.message ?? "Status updated");
                            queryResult.refetch();
                          },
                          onError: (err: any) => {
                            message.error(err?.message ?? "Failed to update status");
                          },
                        }
                      );
                    }}
                  >
                    Update Status
                  </Button>
                </Space>
              </Descriptions.Item>
            </Descriptions>

            {record.description ? (
              <div style={{ marginTop: 16 }}>
                <Typography.Title level={5}>Description</Typography.Title>
                <Typography.Paragraph>{record.description}</Typography.Paragraph>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </Show>
  );
}


